import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import moment from "moment";
import AnalyticsApiService from '../../ApiServices/AnalyticsApiService/AnalyticsApiService';
import { useAuth } from '../Auth/AuthContext';
import { motion } from "framer-motion";

const RevenueChart = () => {
  const [analytics, setAnalytics] = useState([]);
  const [graphType, setGraphType] = useState("ThisMonth"); // ✅ Default to "ThisMonth"
  const [activeBars, setActiveBars] = useState({
    adrevenue: true,
    adimpression: true,
    adrequest: true,
    ecpm: true,
  });
  const [isLoading, setIsLoading] = useState(true); // ✅ Loader state

  const Auth = useAuth();
  const user = Auth.authState?.user;
  const role = Auth.authState?.roles;
  const admin = role === "Admin";

  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true); // ⏳ Start loader
      try {
        const response = await AnalyticsApiService.GetAnalyticsForGraph(admin ? null : user?.id, graphType);
        setAnalytics(response.data || []);
      } catch (error) {
        toast.error("Failed to fetch analytics.");
      } finally {
        setIsLoading(false); // ✅ End loader
      }
    };

    if (user && user.id) {
      fetchAnalytics();
    }
  }, [user, graphType]);

  const chartData = analytics.map((t) => ({
    date: moment(t.date).format("MMM DD"),
    adrevenue: +t.adExchangeRevenue.toFixed(2),
    adimpression: t.adExchangeImpressions,
    adrequest: t.adExchangeAdRequests,
    ecpm: +t.adExchangeAvgECPM.toFixed(2),
  }));

  const handleGraphTypeChange = (event) => {
    setGraphType(event.target.value);
  };

  const handleLegendClick = (event) => {
    const dataKey = event.dataKey;
    setActiveBars((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const resetFilters = () => {
    setActiveBars({
      adrevenue: true,
      adimpression: true,
      adrequest: true,
      ecpm: true,
    });
  };

  return (
    <div className="p-8 rounded-3xl bg-gradient-to-br from-white/70 to-gray-100 shadow-[0_20px_40px_rgba(0,0,0,0.1)] backdrop-blur-xl">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight">
          📊 Revenue & Ad Insights
        </h2>

        <div className="flex gap-4">
          <select
            value={graphType}
            onChange={handleGraphTypeChange}
            className="px-4 py-2 rounded-xl bg-white border border-gray-300 shadow-inner text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="Today">📅 Today</option>
            <option value="Yesterday">📆 Yesterday</option>
            <option value="LastSevenDays">📊 Last 7 Days</option>
            <option value="ThisMonth">📈 This Month</option>
            <option value="LastMonth">📉 Last Month</option>
          </select>

          <button
            onClick={resetFilters}
            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg transition"
          >
            🔄 Reset
          </button>
        </div>
      </div>

      {/* Loader */}
      {isLoading ? (
         <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white/70 backdrop-blur-md p-8 rounded-2xl shadow-xl flex flex-col items-center"
          >
            <div className="w-14 h-14 border-4 border-dashed border-indigo-500 rounded-full animate-spin"></div>
            <p className="mt-4 text-indigo-800 text-base font-semibold tracking-wide animate-pulse">
              Loading analytics data...
            </p>
          </motion.div>
      ) : (
        <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200">
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#4f46e5" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
                <linearGradient id="impressionGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#fbbf24" />
                </linearGradient>
                <linearGradient id="requestGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#059669" />
                  <stop offset="100%" stopColor="#34d399" />
                </linearGradient>
                <linearGradient id="ecpmGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#c084fc" />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fill: "#4b5563", fontSize: 12 }} angle={-20} dy={10} />
              <YAxis yAxisId="left" tickFormatter={(v) => `$${v}`} />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend
                verticalAlign="top"
                align="right"
                wrapperStyle={{ cursor: "pointer", fontSize: 14 }}
                onClick={handleLegendClick}
              />

              {activeBars.adrevenue && (
                <Bar dataKey="adrevenue" name="Revenue" fill="url(#revenueGradient)" yAxisId="left" barSize={20} />
              )}
              {activeBars.adimpression && (
                <Bar dataKey="adimpression" name="Impressions" fill="url(#impressionGradient)" yAxisId="right" barSize={20} />
              )}
              {activeBars.adrequest && (
                <Bar dataKey="adrequest" name="Ad Requests" fill="url(#requestGradient)" yAxisId="right" barSize={20} />
              )}
              {activeBars.ecpm && (
                <Bar dataKey="ecpm" name="Avg eCPM" fill="url(#ecpmGradient)" yAxisId="left" barSize={20} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default RevenueChart;
